/**
 * parseSelectItem / parseOrderItem / parseLimitValue — small textual helpers
 * for the basic SELECT renderer. Token-aware for surrounding-quote handling
 * (still no regex — uses `tokenSkip`). Splits on the trailing `AS alias` /
 * `direction NULLS FIRST/LAST` / numeric value, returning the residual
 * expression text plus the optional metadata.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

interface Token {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly upper: string;
  // True when a depth-0 infix operator (`||`, mssql `+`, etc.) appeared
  // between the previous token and this one — meaning this token is bound
  // into the expression, not a whitespace-separated implicit alias.
  readonly precededByOperator: boolean;
}

function tokenize(source: string, start: number, end: number, options: ParseOptions): Token[] {
  const ctx = skipContextFor(options);
  const out: Token[] = [];
  let i = start;
  let paren = 0;
  let precededByOperator = false;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      // treat string/quoted as a single token (preserve the whole region)
      const text = source.slice(i, skipped);
      out.push({ start: i, end: skipped, text, upper: text.toUpperCase(), precededByOperator });
      precededByOperator = false;
      i = skipped;
      continue;
    }
    const c = source[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren > 0) { i++; continue; }
    if (IDENT_START.test(c)) {
      const s = i;
      i++;
      while (i < end && IDENT_CONT.test(source[i])) i++;
      const text = source.slice(s, i);
      out.push({ start: s, end: i, text, upper: text.toUpperCase(), precededByOperator });
      precededByOperator = false;
      continue;
    }
    // Skip operator characters individually at depth 0 — remember that an
    // infix operator (`||`, mssql string-concat `+`, etc.) was seen so the
    // next token isn't mistaken for a bare alias.
    precededByOperator = true;
    i++;
  }
  return out;
}

export interface ParsedSelectItem {
  readonly expression: string;
  readonly alias: string | null;
}

export function parseSelectItem(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): ParsedSelectItem {
  const text = source.slice(start, end).trim();
  if (text.length === 0) return { expression: '', alias: null };
  const tokens = tokenize(source, start, end, options);
  if (tokens.length === 0) return { expression: text, alias: null };

  const last = tokens[tokens.length - 1];
  const prev = tokens.length >= 2 ? tokens[tokens.length - 2] : null;
  // Alias forms: `expr AS alias` or `expr alias`. Star and parenthesized closes
  // shouldn't be treated as aliases — match only on identifier-shaped last token.
  const isIdentLast = last.text.length > 0 && (IDENT_START.test(last.text[0]) || last.text[0] === '"' || last.text[0] === '`' || last.text[0] === '[');
  if (!isIdentLast) return { expression: text, alias: null };

  if (prev && prev.upper === 'AS') {
    const exprEnd = prev.start;
    return { expression: source.slice(start, exprEnd).trim(), alias: stripAliasQuotes(last.text) };
  }
  // Bare-alias form: only if there are at least 2 tokens AND the last token is
  // not a known keyword.
  if (prev && tokens.length >= 2 && !isKeyword(last.upper)) {
    // A bare alias must be WHITESPACE-separated from the expression. When the
    // last token abuts the previous one with only punctuation — e.g. a
    // qualified name `b.id`, where the dot is dropped during tokenizing so `b`
    // and `id` look like two tokens — it is NOT an alias; keep the whole text
    // as the expression. Likewise, when an infix operator (`||`, mssql `+`,
    // etc.) binds the last token into the expression — e.g.
    // `a || ' ' || b` — it is part of a concatenation, not an alias, even
    // though there is whitespace around the operator.
    const gap = source.slice(prev.end, last.start);
    if (/\s/.test(gap) && !last.precededByOperator) {
      const exprEnd = last.start;
      const exprText = source.slice(start, exprEnd).trim();
      if (exprText.length > 0) {
        return { expression: exprText, alias: stripAliasQuotes(last.text) };
      }
    }
  }
  return { expression: text, alias: null };
}

function isKeyword(upper: string): boolean {
  return KEYWORDS.has(upper);
}

const KEYWORDS: ReadonlySet<string> = new Set([
  'AS', 'ASC', 'DESC', 'NULLS', 'FIRST', 'LAST', 'AND', 'OR', 'NOT', 'NULL',
  'TRUE', 'FALSE', 'IS', 'IN', 'LIKE', 'BETWEEN', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'DISTINCT', 'ALL', 'WITH', 'FROM', 'WHERE', 'GROUP', 'BY', 'HAVING', 'ORDER', 'LIMIT',
  'OFFSET', 'FETCH', 'TOP', 'SELECT', 'UNION', 'INTERSECT', 'EXCEPT', 'JOIN', 'ON',
  'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'USING', 'LATERAL',
  'OVER', 'PARTITION', 'WINDOW', 'ROWS', 'RANGE', 'GROUPS',
]);

function stripAliasQuotes(raw: string): string {
  if (raw.length >= 2) {
    const first = raw[0];
    const last = raw[raw.length - 1];
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return raw.slice(1, -1).replace(/""/g, '"');
    }
    if (first === '[' && last === ']') return raw.slice(1, -1);
  }
  return raw;
}

export interface ParsedOrderItem {
  readonly expression: string;
  readonly direction: 'ASC' | 'DESC' | null;
  readonly nulls: 'FIRST' | 'LAST' | null;
}

export function parseOrderItem(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): ParsedOrderItem {
  const text = source.slice(start, end).trim();
  if (text.length === 0) return { expression: '', direction: null, nulls: null };
  const tokens = tokenize(source, start, end, options);
  let direction: 'ASC' | 'DESC' | null = null;
  let nulls: 'FIRST' | 'LAST' | null = null;
  let cutoff = end;
  for (let i = tokens.length - 1; i >= 0; i--) {
    const t = tokens[i];
    if ((t.upper === 'FIRST' || t.upper === 'LAST') && i > 0 && tokens[i - 1].upper === 'NULLS') {
      nulls = t.upper as 'FIRST' | 'LAST';
      cutoff = Math.min(cutoff, tokens[i - 1].start);
      i--;
      continue;
    }
    if (t.upper === 'ASC' || t.upper === 'DESC') {
      direction = t.upper as 'ASC' | 'DESC';
      cutoff = Math.min(cutoff, t.start);
      continue;
    }
    break;
  }
  return { expression: source.slice(start, cutoff).trim(), direction, nulls };
}
